import { z } from 'zod';

export const MAX_IMAGE_BYTES = 850 * 1024;
export const BOOKING_STATUSES = ['waiting', 'called', 'completed', 'cancelled'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

const shortText = (max: number) => z.string().trim().min(1).max(max);

export const bookingSchema = z.object({
  event_id: z.string().min(8).max(100),
  public_token: z.string().min(16).max(200),
  idempotency_key: z.uuid(),
  receipt_token: z.string().min(32).max(300),
  stencil_id: z.string().min(8).max(100),
  first_name: shortText(80),
  last_name: shortText(80),
  phone: z.string().trim().min(5).max(30).regex(/^[+0-9 ()\-./]+$/),
  has_allergies: z.boolean(),
  allergies_details: z.string().trim().max(500).optional().default(''),
  takes_medication: z.boolean(),
  medication_details: z.string().trim().max(500).optional().default(''),
  privacy_accepted: z.literal(true),
  turnstile_token: z.string().min(1).max(2048)
}).superRefine((value, ctx) => {
  if (value.has_allergies && !value.allergies_details) {
    ctx.addIssue({ code: 'custom', path: ['allergies_details'], message: 'Specifica le allergie' });
  }
  if (value.takes_medication && !value.medication_details) {
    ctx.addIssue({ code: 'custom', path: ['medication_details'], message: 'Specifica i farmaci' });
  }
});

export const themeSchema = z.object({
  welcome_title: shortText(100),
  welcome_signature: shortText(60),
  welcome_message: z.string().trim().max(300),
  background_mode: z.enum(['color', 'image']),
  background_color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  background_image_id: z.string().min(8).max(100).nullable(),
  background_position: z.enum(['center', 'top', 'bottom']),
  background_overlay: z.number().min(0).max(0.8),
  hero_image_id: z.string().min(8).max(100).nullable(),
  hero_image_alt: z.string().trim().min(1).max(120),
  text_theme: z.enum(['light', 'champagne']),
  max_bookings_per_phone: z.number().int().min(1).max(20)
});

export const eventSchema = z.object({
  couple_name: shortText(100),
  welcome_title: shortText(100),
  welcome_message: z.string().trim().max(300),
  copy_theme: z.boolean().default(false)
});

export const stencilCreateSchema = z.object({
  name: shortText(80),
  image_id: z.string().min(8).max(100)
});

export function normalizePhone(value: string): string {
  return value.replace(/\D/g, '');
}

export function isWebP(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  return String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' &&
    String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
}

export function neutralizeCsvCell(value: unknown): string {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

export function bookingsToCsv(rows: Record<string, unknown>[]): string {
  const columns = [
    ['queue_number', 'Numero'], ['first_name', 'Nome'], ['last_name', 'Cognome'],
    ['phone', 'Telefono'], ['stencil_name_snapshot', 'Stencil'], ['allergies', 'Allergie'],
    ['medication', 'Farmaci'], ['status', 'Stato'], ['created_at', 'Data e ora']
  ] as const;
  const header = columns.map(([, label]) => neutralizeCsvCell(label)).join(';');
  const body = rows.map((row) => columns.map(([key]) => neutralizeCsvCell(row[key])).join(';'));
  return `\uFEFF${[header, ...body].join('\r\n')}\r\n`;
}
